import { type z, ZodError, type ZodType } from "zod";

import {
  dailyPodcastPolicyResponseSchema,
  dailyPodcastPolicyUpdateRequestSchema,
  dailyPodcastRepublishResponseSchema,
  parseContract,
  powerBIScheduleCreateRequestSchema,
  powerBIScheduleCreateResponseSchema,
  powerBIScheduleListResponseSchema,
  powerBIScheduleResponseSchema,
  powerBIScheduleUpdateRequestSchema,
  reportDeliveryAudienceUpdateRequestSchema,
  whatsAppDeliverySettingsUpdateRequestSchema,
  whatsAppUserMappingCreateRequestSchema,
  whatsAppUserMappingListResponseSchema,
  whatsAppUserMappingResponseSchema,
  whatsAppUserMappingUpdateRequestSchema,
  workflowOccurrenceAcceptedSchema,
  workflowOccurrenceDetailResponseSchema,
  workflowOccurrenceListResponseSchema,
} from "@/lib/api-contract";
import { getMessages } from "@/lib/i18n";
import { useAdminStore } from "@/store/admin-store";

import type { components } from "@/lib/generated/openapi";

type ApiSchemas = components["schemas"];

export interface ApiValidationIssue {
  loc: (string | number)[];
  msg: string;
  type: string;
}

export type ApiErrorDetail = string | ApiValidationIssue[];

export interface ApiErrorResponse {
  detail: ApiErrorDetail;
}

export interface ApiErrorShape {
  detail?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function extractValidationIssues(payload: unknown): ApiValidationIssue[] {
  if (!payload || typeof payload !== "object" || !("detail" in payload)) {
    return [];
  }
  const detail = (payload as ApiErrorShape).detail;
  if (!Array.isArray(detail)) {
    return [];
  }

  return detail.flatMap((entry): ApiValidationIssue[] => {
    if (!isRecord(entry) || !Array.isArray(entry.loc) || typeof entry.msg !== "string") {
      return [];
    }
    const loc = entry.loc.filter(
      (part): part is string | number => typeof part === "string" || typeof part === "number",
    );
    if (loc.length === 0) {
      return [];
    }
    return [
      {
        loc,
        msg: entry.msg,
        type: typeof entry.type === "string" ? entry.type : "validation_error",
      },
    ];
  });
}

function extractValidationFieldErrors(payload: unknown) {
  const errors: Record<string, string[]> = {};
  for (const issue of extractValidationIssues(payload)) {
    const location = [...issue.loc];
    if (location[0] === "body" || location[0] === "query" || location[0] === "path") {
      location.shift();
    }
    if (location.length === 0) {
      continue;
    }
    const key = location.map(String).join(".");
    errors[key] = [...(errors[key] ?? []), issue.msg];
  }
  return errors;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly fieldErrors: Record<string, string[]>;
  readonly validationIssues: ApiValidationIssue[];

  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.fieldErrors = extractValidationFieldErrors(payload);
    this.validationIssues = extractValidationIssues(payload);
  }
}

export class ApiTransportError extends Error {
  readonly method: string;
  readonly path: string;

  constructor(method: string, path: string) {
    super("The request could not reach the API.");
    this.name = "ApiTransportError";
    this.method = method;
    this.path = path;
  }
}

export type ApiResponseErrorReason =
  "missing_json" | "malformed_json" | "schema_validation" | "unexpected_status" | "missing_location";

export class ApiResponseError extends Error {
  readonly reason: ApiResponseErrorReason;
  readonly status: number;
  readonly issues: { path: (string | number)[]; message: string }[];

  constructor(
    reason: ApiResponseErrorReason,
    status: number,
    message: string,
    issues: { path: (string | number)[]; message: string }[] = [],
  ) {
    super(message);
    this.name = "ApiResponseError";
    this.reason = reason;
    this.status = status;
    this.issues = issues;
  }
}

export class ApiRequestValidationError extends Error {
  readonly fieldErrors: Record<string, string[]>;

  constructor(error: ZodError) {
    super("The request does not match the API contract.");
    this.name = "ApiRequestValidationError";
    this.fieldErrors = Object.fromEntries(
      Object.entries(
        error.issues.reduce<Record<string, string[]>>((result, issue) => {
          const key = issue.path.map(String).join(".") || "request";
          result[key] = [...(result[key] ?? []), issue.message];
          return result;
        }, {}),
      ),
    );
  }
}

export interface DemoProfilesResponse {
  users: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
    roleDisplayName?: string | null;
    profile: string;
    accessLevel?: "read" | "read_write" | "admin" | null;
    permissions: string[];
    centers?: string[];
  }[];
}

export interface DemoLoginResponse {
  sessionToken: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
    roleDisplayName?: string | null;
    profile: string;
    accessLevel?: "read" | "read_write" | "admin" | null;
    permissions: string[];
  };
}

export interface BusinessRoleDefinition {
  role: UserRole;
  displayName?: string | null;
  profile: string;
  permissions: string[];
  centers: string[];
}

export interface BusinessRoleDefinitionListResponse {
  roles: BusinessRoleDefinition[];
  availablePermissions: string[];
}

export type ReportSummary = ApiSchemas["PublishedReportResponse"];
export type ReportListResponse = ApiSchemas["ReportListResponse"];
export type ExtractedMetric = ApiSchemas["ExtractedMetric"];
export type MetricListResponse = ApiSchemas["MetricListResponse"];
export type Insight = ApiSchemas["Insight"];
export type InsightListResponse = ApiSchemas["InsightListResponse"];
export type ReportDetailResponse = ApiSchemas["ReportDetailResponse"];

export type UserRole = ApiSchemas["UserRole"];

export interface WhatsAppUserMapping {
  id: string;
  phoneNumber: string;
  userId: string;
  center?: string | null;
  role?: UserRole | null;
  active: boolean;
  activatedAt?: string | null;
  reportPodcastEnabled: boolean;
  reportPodcastEnabledAt: string | null;
  dailyPodcastEnabled: boolean;
  dailyPodcastEnabledAt: string | null;
}

export interface WhatsAppUserMappingCreateRequest {
  phoneNumber: string;
  userId: string;
  center?: string | null;
  role?: UserRole | null;
  active?: boolean;
  reportPodcastEnabled?: boolean;
  dailyPodcastEnabled?: boolean;
}

export interface WhatsAppUserMappingUpdateRequest {
  phoneNumber: string;
  userId: string;
  center?: string | null;
  role?: UserRole | null;
  active: boolean;
}

export interface WhatsAppDeliverySettingsUpdateRequest {
  active?: boolean;
  reportPodcastEnabled?: boolean;
  dailyPodcastEnabled?: boolean;
}

export interface WhatsAppDelivery {
  id: string;
  profile?: string | null;
  reportId?: string | null;
  deliveryKind?: "report_podcast" | "daily_podcast" | null;
  episodeId?: string | null;
  mappingId?: string | null;
  channel?: string | null;
  trigger?: string | null;
  testMode?: boolean;
  messageBody?: string | null;
  mediaUrls?: string[];
  twilioMessageSid?: string | null;
  recipient: string;
  status: string;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppNotificationResponse {
  delivery: WhatsAppDelivery;
  deliveries?: WhatsAppDelivery[];
}

export interface WhatsAppUserMappingListResponse {
  mappings: WhatsAppUserMapping[];
}

export interface WhatsAppMessageTraceListResponse {
  messages: {
    inboundMessage: {
      id: string;
      sender: string;
      recipient: string;
      messageBody?: string | null;
      numMedia: number;
      transcribedText?: string | null;
      mappedUserName?: string | null;
      inferredCenter?: string | null;
      inferredRole?: string | null;
      autoReplyStatus: string;
      receivedAt: string;
    };
    replyDelivery?: {
      id: string;
      recipient: string;
      status: string;
      errorMessage?: string | null;
      createdAt: string;
    } | null;
  }[];
}

export interface WhatsAppDeliveryListResponse {
  deliveries: WhatsAppDelivery[];
}

export type ProviderStatusResponse = ApiSchemas["ProviderStatusResponse"];
export type PowerBIStatusResponse = ApiSchemas["PowerBIStatusResponse"];
export type PowerBIReportSummary = ApiSchemas["PowerBIReportSummary"];
export type PowerBIReportListResponse = ApiSchemas["PowerBIReportListResponse"];
export type PowerBIReportPageSummary = ApiSchemas["PowerBIReportPageSummary"];
export type PowerBIReportPageListResponse = ApiSchemas["PowerBIReportPageListResponse"];
export type PowerBIReportCatalogEntry = ApiSchemas["PowerBIReportCatalogEntry"];
export type PowerBICatalogResponse = ApiSchemas["PowerBICatalogResponse"];
export type PowerBIWorkspace = ApiSchemas["PowerBIWorkspace"];
export type PowerBIWorkspaceListResponse = ApiSchemas["PowerBIWorkspaceListResponse"];

export type ISODateTime = string;
export type WorkflowKind = ApiSchemas["WorkflowKind"];
export type WorkflowOccurrenceStatus = ApiSchemas["OccurrenceStatus"];
export type WorkflowStageStatus = ApiSchemas["StageStatus"];
export type WorkflowStageName = ApiSchemas["StageName"];
export type WorkflowFailureCategory = ApiSchemas["FailureCategory"];
export type WorkflowAttemptOutcome = ApiSchemas["AttemptOutcome"];
export type WorkflowOutboxStatus = ApiSchemas["OutboxStatus"];
export type WorkflowConfigSnapshot = ApiSchemas["WorkflowOccurrence"]["configSnapshot"];
export type WorkflowStageCheckpoint = NonNullable<ApiSchemas["WorkflowStageState"]["checkpoint"]>;

export type WorkflowExecutionProfile = ApiSchemas["ExecutionProfile"];
export type WorkflowFailure = ApiSchemas["StageFailure"];
export type WorkflowRetryPolicy = ApiSchemas["RetryPolicy"];
export type WorkflowStageState = ApiSchemas["WorkflowStageState"];
export type WorkflowTrigger = ApiSchemas["WorkflowOccurrence"]["trigger"];
export type WorkflowOccurrence = ApiSchemas["WorkflowOccurrence"];
export type WorkflowOccurrenceSummary = ApiSchemas["WorkflowOccurrenceListItemResponse"];
export type WorkflowOccurrenceAccepted = ApiSchemas["WorkflowOccurrenceAcceptedResponse"];
export type WorkflowOccurrenceListResponse = ApiSchemas["WorkflowOccurrenceListResponse"];
export type WorkflowStageAttempt = ApiSchemas["WorkflowStageAttempt"];
export type WorkflowAuditEvent = ApiSchemas["WorkflowAuditEvent"];
export type WorkflowOutboxEvent = ApiSchemas["WorkflowOutboxEvent"];
export type WorkflowOccurrenceDetailResponse = ApiSchemas["WorkflowOccurrenceDetailResponse"];

export type PowerBIScheduleFrequency = ApiSchemas["PowerBIScheduleFrequency"];

export type PowerBIWeekday = ApiSchemas["PowerBIWeekday"];

export type PowerBIInvalidDayPolicy = ApiSchemas["PowerBIInvalidDayPolicy"];

export type PowerBIMisfirePolicy = ApiSchemas["PowerBIMisfirePolicy"];

export interface PowerBIWeeklySchedule {
  weekdays: PowerBIWeekday[];
}

export interface PowerBIMonthlySchedule {
  day: number;
  invalidDayPolicy?: PowerBIInvalidDayPolicy;
}

interface PowerBIScheduleUpdateBase {
  reportType: string;
  reportId: string;
  reportName: string;
  pageName: string;
  pageDisplayName: string | null;
  timeOfDay: string;
  workspaceId: string;
  locale: string | null;
  active: boolean;
  misfirePolicy: PowerBIMisfirePolicy;
  catchUpLimit: number;
  recipientRoles?: UserRole[];
  includedUserIds?: string[];
  excludedUserIds?: string[];
}

export type PowerBIScheduleUpdateRequest =
  | (PowerBIScheduleUpdateBase & {
      frequency: "daily";
      weekly: null;
      monthly: null;
    })
  | (PowerBIScheduleUpdateBase & {
      frequency: "weekly";
      weekly: PowerBIWeeklySchedule;
      monthly: null;
    })
  | (PowerBIScheduleUpdateBase & {
      frequency: "monthly";
      weekly: null;
      monthly: PowerBIMonthlySchedule;
    });

export type ReportContextCreateRequest = ApiSchemas["ReportContextCreateRequest"];
interface PowerBIScheduleCreateBase {
  reportType: string;
  reportId: string;
  reportName: string;
  pageName: string;
  timeOfDay: string;
  workspaceId: string;
  pageDisplayName?: string | null;
  locale?: string | null;
  active?: boolean;
  misfirePolicy?: PowerBIMisfirePolicy;
  catchUpLimit?: number;
  recipientRoles?: UserRole[];
  includedUserIds?: string[];
  excludedUserIds?: string[];
  context: ReportContextCreateRequest;
}

export type PowerBIScheduleCreateRequest =
  | (PowerBIScheduleCreateBase & { frequency?: "daily"; weekly?: null; monthly?: null })
  | (PowerBIScheduleCreateBase & {
      frequency: "weekly";
      weekly: PowerBIWeeklySchedule;
      monthly?: null;
    })
  | (PowerBIScheduleCreateBase & {
      frequency: "monthly";
      weekly?: null;
      monthly: PowerBIMonthlySchedule;
    });
export type ReportContextResponse = Omit<
  ApiSchemas["ReportContextResponse"],
  | "siteIds"
  | "audience"
  | "readingOrder"
  | "keyMetrics"
  | "interpretationRules"
  | "expertProfile"
  | "expectedRecommendations"
  | "communicationStyle"
  | "additionalGuidance"
> & {
  siteIds: string[];
  audience: string | null;
  readingOrder: string | null;
  keyMetrics: ApiSchemas["KeyMetric"][];
  interpretationRules: ApiSchemas["InterpretationRule"][];
  expertProfile: string | null;
  expectedRecommendations: string | null;
  communicationStyle: string | null;
  additionalGuidance: string | null;
};
export type PowerBISchedule = Omit<
  ApiSchemas["PowerBISchedule"],
  | "frequency"
  | "pageDisplayName"
  | "timeOfDay"
  | "weekly"
  | "monthly"
  | "locale"
  | "active"
  | "revision"
  | "misfirePolicy"
  | "catchUpLimit"
  | "recipientRoles"
  | "includedUserIds"
  | "excludedUserIds"
  | "nextMaterializationAt"
  | "cadenceEffectiveAt"
> & {
  frequency: PowerBIScheduleFrequency;
  pageDisplayName: string | null;
  timeOfDay: string | null;
  weekly: PowerBIWeeklySchedule | null;
  monthly: PowerBIMonthlySchedule | null;
  locale: string | null;
  active: boolean;
  revision: number;
  misfirePolicy: PowerBIMisfirePolicy;
  catchUpLimit: number;
  recipientRoles: UserRole[];
  includedUserIds: string[];
  excludedUserIds: string[];
  nextMaterializationAt: string | null;
  cadenceEffectiveAt: string | null;
};
export interface PowerBIScheduleCreateResponse {
  schedule: PowerBISchedule;
  context: ReportContextResponse;
}
export interface PowerBIScheduleResponse {
  schedule: PowerBISchedule;
}
export interface PowerBIScheduleListResponse {
  schedules: PowerBISchedule[];
}

export interface ReportDeliveryAudienceUpdateRequest {
  recipientRoles: UserRole[];
  includedUserIds: string[];
  excludedUserIds: string[];
}

export interface ApiOperationResult<T, Status extends 201 | 202> {
  data: T;
  status: Status;
  location: string;
}

export type DailyIncompletePolicy = ApiSchemas["DailyPodcastIncompletePolicy"];
export type DailyLateArrivalPolicy = ApiSchemas["DailyPodcastLateArrivalPolicy"];

export type DailyPodcastPolicy = Required<ApiSchemas["DailyPodcastPolicy"]>;
export type DailyPodcastPolicyUpdate = ApiSchemas["DailyPodcastPolicyUpdateRequest"];
export interface DailyPodcastPolicyResponse {
  policy: DailyPodcastPolicy;
}

export type PodcastEpisode = ApiSchemas["PodcastEpisode"];
export type PodcastTodayResponse = ApiSchemas["PodcastTodayResponse"];
export type PodcastHistoryResponse = ApiSchemas["PodcastHistoryResponse"];

async function parseJsonOrNull(response: Response) {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return null;
  }
  return (await response.json().catch(() => null)) as unknown;
}

function normalizeErrorMessage(
  status: number,
  payload: unknown,
  messages: Pick<
    ReturnType<typeof getMessages>["common"],
    "loadingError" | "providerFailure" | "serviceUnavailable" | "requestTimeout"
  >,
) {
  if (status === 502) {
    return messages.providerFailure;
  }
  if (status === 503) {
    return messages.serviceUnavailable;
  }
  if (status === 504) {
    return messages.requestTimeout;
  }
  if (
    payload &&
    typeof payload === "object" &&
    "detail" in payload &&
    typeof (payload as ApiErrorShape).detail === "string"
  ) {
    return (payload as ApiErrorShape).detail as string;
  }
  const fieldErrors = extractValidationFieldErrors(payload);
  const firstMessage = Object.values(fieldErrors)[0]?.[0];
  if (firstMessage) {
    return firstMessage;
  }
  return messages.loadingError;
}

export async function apiFetchResponse(path: string, init?: RequestInit): Promise<Response> {
  const { sessionToken, locale } = useAdminStore.getState();
  const messages = getMessages(locale);
  const headers = new Headers(init?.headers);
  headers.set("accept", "application/json");
  headers.set("x-aquaportal-locale", locale);
  if (sessionToken) {
    headers.set("x-demo-session", sessionToken);
  }
  if (init?.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const method = init?.method ?? "GET";
  let response: Response;
  try {
    response = await fetch(`/api/v1${path}`, {
      ...init,
      headers,
      cache: "no-store",
    });
  } catch {
    throw new ApiTransportError(method, path);
  }

  if (!response.ok) {
    const payload = await parseJsonOrNull(response);
    throw new ApiRequestError(
      response.status,
      normalizeErrorMessage(response.status, payload, messages.common),
      payload,
    );
  }
  return response;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetchResponse(path, init);
  return (await parseJsonOrNull(response)) as T;
}

function serializeRequest(schema: ZodType, payload: unknown) {
  try {
    return JSON.stringify(parseContract(schema, payload));
  } catch (error) {
    if (error instanceof ZodError) {
      throw new ApiRequestValidationError(error);
    }
    throw error;
  }
}

export function serializeApiRequest(schema: ZodType, payload: unknown) {
  return serializeRequest(schema, payload);
}

async function decodeJsonResponse<T>(response: Response, schema: ZodType, expectedStatus: number): Promise<T> {
  if (response.status !== expectedStatus) {
    throw new ApiResponseError(
      "unexpected_status",
      response.status,
      `Expected HTTP ${expectedStatus}, received ${response.status}.`,
    );
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new ApiResponseError("missing_json", response.status, "The API response did not contain JSON.");
  }
  const body = await response.text();
  let payload: unknown;
  try {
    payload = JSON.parse(body) as unknown;
  } catch {
    throw new ApiResponseError("malformed_json", response.status, "The API returned malformed JSON.");
  }
  try {
    return parseContract(schema, payload) as T;
  } catch (error) {
    if (error instanceof ZodError) {
      throw new ApiResponseError(
        "schema_validation",
        response.status,
        "The API response did not match the canonical contract.",
        error.issues.map((issue) => ({
          path: issue.path.filter(
            (part): part is string | number => typeof part === "string" || typeof part === "number",
          ),
          message: issue.message,
        })),
      );
    }
    throw error;
  }
}

export async function apiFetchValidated<T>(path: string, schema: ZodType, init?: RequestInit, expectedStatus = 200) {
  const response = await apiFetchResponse(path, init);
  return decodeJsonResponse<T>(response, schema, expectedStatus);
}

function requiredLocation(response: Response, expectedSuffix: string) {
  const location = response.headers.get("location");
  if (!location?.endsWith(expectedSuffix)) {
    throw new ApiResponseError("missing_location", response.status, "The API response did not identify its resource.");
  }
  return location;
}

export function listDemoProfiles() {
  return apiFetch<DemoProfilesResponse>("/auth/demo-profiles");
}

export function listBusinessRoles() {
  return apiFetch<BusinessRoleDefinitionListResponse>("/business-roles");
}

export function updateBusinessRole(
  role: string,
  payload: { displayName: string; profile: string; permissions: string[]; centers: string[] },
) {
  return apiFetch<{ roleDefinition: BusinessRoleDefinition }>(`/business-roles/${encodeURIComponent(role)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function demoLogin(userId: string) {
  return apiFetch<DemoLoginResponse>("/auth/demo-login", {
    method: "POST",
    body: JSON.stringify({ userId }),
  });
}

export function listReports() {
  return apiFetch<ReportListResponse>("/reports");
}

export function getReportDetails(reportId: string) {
  return apiFetch<ReportDetailResponse>(`/reports/${encodeURIComponent(reportId)}`);
}

function appendOptionalQuery(query: URLSearchParams, key: string, value: string | undefined) {
  if (value !== undefined && value !== "") {
    query.set(key, value);
  }
}

export function listMetrics(
  params: {
    center?: string;
    jaula?: string;
    metric?: string;
    startDate?: string;
    endDate?: string;
    reportId?: string;
  } = {},
) {
  const query = new URLSearchParams();
  appendOptionalQuery(query, "center", params.center);
  appendOptionalQuery(query, "jaula", params.jaula);
  appendOptionalQuery(query, "metric", params.metric);
  appendOptionalQuery(query, "start_date", params.startDate);
  appendOptionalQuery(query, "end_date", params.endDate);
  appendOptionalQuery(query, "report_id", params.reportId);
  const suffix = query.size ? `?${query.toString()}` : "";
  return apiFetch<MetricListResponse>(`/metrics${suffix}`);
}

export function listInsights(
  params: {
    center?: string;
    priority?: ApiSchemas["Severity"];
    role?: ApiSchemas["UserRole"];
    startDate?: string;
    endDate?: string;
  } = {},
) {
  const query = new URLSearchParams();
  appendOptionalQuery(query, "center", params.center);
  appendOptionalQuery(query, "priority", params.priority);
  appendOptionalQuery(query, "role", params.role);
  appendOptionalQuery(query, "start_date", params.startDate);
  appendOptionalQuery(query, "end_date", params.endDate);
  const suffix = query.size ? `?${query.toString()}` : "";
  return apiFetch<InsightListResponse>(`/insights${suffix}`);
}

export async function getTodayPodcast() {
  try {
    return await apiFetch<PodcastTodayResponse>("/podcast/today");
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export function listPodcastHistory() {
  return apiFetch<PodcastHistoryResponse>("/podcast/history");
}

export function getProviderStatus() {
  return apiFetch<ProviderStatusResponse>("/admin/providers");
}

export async function getDailyPodcastPolicy() {
  const response = await apiFetchResponse("/admin/daily-podcast-policy");
  return decodeJsonResponse<DailyPodcastPolicyResponse>(response, dailyPodcastPolicyResponseSchema, 200);
}

export async function updateDailyPodcastPolicy(payload: DailyPodcastPolicyUpdate) {
  const response = await apiFetchResponse("/admin/daily-podcast-policy", {
    method: "PUT",
    body: serializeRequest(dailyPodcastPolicyUpdateRequestSchema, payload),
  });
  return decodeJsonResponse<DailyPodcastPolicyResponse>(response, dailyPodcastPolicyResponseSchema, 200);
}

export async function listWorkflows(
  params: {
    kind?: WorkflowKind;
    status?: WorkflowOccurrenceStatus;
    limit?: number;
    cursor?: string;
  } = {},
) {
  const query = new URLSearchParams();
  if (params.kind) {
    query.set("kind", params.kind);
  }
  if (params.status) {
    query.set("status", params.status);
  }
  if (params.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  if (params.cursor !== undefined) {
    query.set("cursor", params.cursor);
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await apiFetchResponse(`/admin/workflows${suffix}`);
  return decodeJsonResponse<WorkflowOccurrenceListResponse>(response, workflowOccurrenceListResponseSchema, 200);
}

export async function getWorkflow(id: string) {
  const response = await apiFetchResponse(`/admin/workflows/${encodeURIComponent(id)}`);
  return decodeJsonResponse<WorkflowOccurrenceDetailResponse>(response, workflowOccurrenceDetailResponseSchema, 200);
}

function normalizeWorkflowActionReason(reason: string) {
  const normalized = reason.trim();
  if (normalized.length === 0 || normalized.length > 1_000) {
    throw new RangeError("Workflow action reasons must contain 1 to 1,000 characters.");
  }
  return normalized;
}

export function retryWorkflow(id: string, reason: string) {
  return apiFetchValidated<WorkflowOccurrenceAccepted>(
    `/admin/workflows/${encodeURIComponent(id)}/retry`,
    workflowOccurrenceAcceptedSchema,
    {
      method: "POST",
      body: JSON.stringify({ reason: normalizeWorkflowActionReason(reason) }),
    },
  );
}

export function cancelWorkflow(id: string, reason: string) {
  return apiFetchValidated<WorkflowOccurrenceAccepted>(
    `/admin/workflows/${encodeURIComponent(id)}/cancel`,
    workflowOccurrenceAcceptedSchema,
    {
      method: "POST",
      body: JSON.stringify({ reason: normalizeWorkflowActionReason(reason) }),
    },
  );
}

export function rerunWorkflowStage(id: string, input: { reason: string; stage?: WorkflowStageName | null }) {
  return apiFetchValidated<WorkflowOccurrenceAccepted>(
    `/admin/workflows/${encodeURIComponent(id)}/rerun`,
    workflowOccurrenceAcceptedSchema,
    {
      method: "POST",
      body: JSON.stringify({ reason: normalizeWorkflowActionReason(input.reason), stage: input.stage }),
    },
  );
}

export function retryWorkflowDelivery(occurrenceId: string, eventId: string, reason: string) {
  return apiFetchValidated<WorkflowOccurrenceDetailResponse>(
    `/admin/workflows/${encodeURIComponent(occurrenceId)}/outbox/${encodeURIComponent(eventId)}/retry`,
    workflowOccurrenceDetailResponseSchema,
    {
      method: "POST",
      body: JSON.stringify({ reason: normalizeWorkflowActionReason(reason) }),
    },
  );
}

export type DailyPodcastRepublishResponse = z.infer<typeof dailyPodcastRepublishResponseSchema>;

export interface DailyPodcastRepublishOptions {
  skipWhatsapp?: boolean;
  forceRag?: boolean;
}

function buildDailyRepublishUrl(
  resource: { kind: "episode" | "occurrence"; id: string },
  options: DailyPodcastRepublishOptions | undefined,
) {
  const params = new URLSearchParams();
  const skip = options?.skipWhatsapp;
  const force = options?.forceRag;
  if (skip !== undefined) params.set("skip_whatsapp", skip ? "true" : "false");
  if (force !== undefined) params.set("force_rag", force ? "true" : "false");
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return `/admin/daily-podcast/by-${resource.kind}/${encodeURIComponent(resource.id)}/republish${suffix}`;
}

export function republishDailyPodcastByEpisode(episodeId: string, options?: DailyPodcastRepublishOptions) {
  return apiFetchValidated<DailyPodcastRepublishResponse>(
    buildDailyRepublishUrl({ kind: "episode", id: episodeId }, options),
    dailyPodcastRepublishResponseSchema,
    { method: "POST" },
  );
}

export function republishDailyPodcastByOccurrence(occurrenceId: string, options?: DailyPodcastRepublishOptions) {
  return apiFetchValidated<DailyPodcastRepublishResponse>(
    buildDailyRepublishUrl({ kind: "occurrence", id: occurrenceId }, options),
    dailyPodcastRepublishResponseSchema,
    { method: "POST" },
  );
}

function normalizePowerBIWorkspaceId(value: string) {
  const normalized = value.trim();
  if (normalized.length === 0 || normalized.length > 120) {
    throw new RangeError("Power BI workspace IDs must contain 1 to 120 characters.");
  }
  return normalized;
}

function powerBIWorkspaceQuery(workspaceId: string) {
  return `?${new URLSearchParams({ workspaceId: normalizePowerBIWorkspaceId(workspaceId) }).toString()}`;
}

export function listPowerBIWorkspaces() {
  return apiFetch<PowerBIWorkspaceListResponse>("/admin/powerbi/workspaces");
}

export function registerPowerBIWorkspace(workspaceId: string) {
  const id = normalizePowerBIWorkspaceId(workspaceId);
  return apiFetch<PowerBIWorkspace>(`/admin/powerbi/workspaces/${encodeURIComponent(id)}`, {
    method: "PUT",
  });
}

export async function deletePowerBIWorkspace(workspaceId: string): Promise<void> {
  const id = normalizePowerBIWorkspaceId(workspaceId);
  await apiFetch<unknown>(`/admin/powerbi/workspaces/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getPowerBIStatus(workspaceId: string) {
  return apiFetch<PowerBIStatusResponse>(`/admin/powerbi/status${powerBIWorkspaceQuery(workspaceId)}`);
}

export function listPowerBIReports(workspaceId: string) {
  return apiFetch<PowerBIReportListResponse>(`/admin/powerbi/reports${powerBIWorkspaceQuery(workspaceId)}`);
}

export function listPowerBIReportPages(reportId: string, workspaceId: string) {
  const q = powerBIWorkspaceQuery(workspaceId);
  return apiFetch<PowerBIReportPageListResponse>(`/admin/powerbi/reports/${encodeURIComponent(reportId)}/pages${q}`);
}

export function getPowerBICatalog(workspaceId: string) {
  return apiFetch<PowerBICatalogResponse>(`/admin/powerbi/catalog${powerBIWorkspaceQuery(workspaceId)}`);
}

export function getPublicPowerBICatalog(workspaceId: string) {
  return apiFetch<PowerBICatalogResponse>(`/powerbi/catalog${powerBIWorkspaceQuery(workspaceId)}`);
}

function normalizeIdempotencyKey(value: string) {
  const normalized = value.trim();
  if (normalized.length === 0 || normalized.length > 200) {
    throw new RangeError("Idempotency keys must contain 1 to 200 characters.");
  }
  return normalized;
}

export async function listPowerBISchedules() {
  const response = await apiFetchResponse("/admin/powerbi-schedules");
  return decodeJsonResponse<PowerBIScheduleListResponse>(response, powerBIScheduleListResponseSchema, 200);
}

export async function createPowerBISchedule(
  payload: PowerBIScheduleCreateRequest,
): Promise<ApiOperationResult<PowerBIScheduleCreateResponse, 201>> {
  const response = await apiFetchResponse("/admin/powerbi-schedules", {
    method: "POST",
    body: serializeRequest(powerBIScheduleCreateRequestSchema, payload),
  });
  const data = await decodeJsonResponse<PowerBIScheduleCreateResponse>(
    response,
    powerBIScheduleCreateResponseSchema,
    201,
  );
  return { data, status: 201, location: requiredLocation(response, `/${data.schedule.id}`) };
}

export async function getPowerBISchedule(scheduleId: string) {
  const response = await apiFetchResponse(`/admin/powerbi-schedules/${encodeURIComponent(scheduleId)}`);
  return decodeJsonResponse<PowerBIScheduleResponse>(response, powerBIScheduleResponseSchema, 200);
}

export async function updatePowerBISchedule(scheduleId: string, payload: PowerBIScheduleUpdateRequest) {
  const response = await apiFetchResponse(`/admin/powerbi-schedules/${encodeURIComponent(scheduleId)}`, {
    method: "PATCH",
    body: serializeRequest(powerBIScheduleUpdateRequestSchema, payload),
  });
  return decodeJsonResponse<PowerBIScheduleResponse>(response, powerBIScheduleResponseSchema, 200);
}

export async function updatePowerBIScheduleDeliveryAudience(
  scheduleId: string,
  payload: ReportDeliveryAudienceUpdateRequest,
) {
  const response = await apiFetchResponse(
    `/admin/powerbi-schedules/${encodeURIComponent(scheduleId)}/delivery-audience`,
    {
      method: "PUT",
      body: serializeRequest(reportDeliveryAudienceUpdateRequestSchema, payload),
    },
  );
  return decodeJsonResponse<PowerBIScheduleResponse>(response, powerBIScheduleResponseSchema, 200);
}

export async function deletePowerBISchedule(scheduleId: string): Promise<void> {
  const response = await apiFetchResponse(`/admin/powerbi-schedules/${encodeURIComponent(scheduleId)}`, {
    method: "DELETE",
  });
  if (response.status !== 204) {
    throw new ApiResponseError("unexpected_status", response.status, `Expected HTTP 204, received ${response.status}.`);
  }
}

export async function runPowerBISchedule(
  scheduleId: string,
  options: { idempotencyKey?: string } = {},
): Promise<ApiOperationResult<WorkflowOccurrenceAccepted, 202>> {
  const headers = new Headers();
  if (options.idempotencyKey !== undefined) {
    headers.set("Idempotency-Key", normalizeIdempotencyKey(options.idempotencyKey));
  }
  const response = await apiFetchResponse(`/admin/powerbi-schedules/${encodeURIComponent(scheduleId)}/run`, {
    method: "POST",
    headers,
  });
  const data = await decodeJsonResponse<WorkflowOccurrenceAccepted>(response, workflowOccurrenceAcceptedSchema, 202);
  return { data, status: 202, location: requiredLocation(response, `/${data.id}`) };
}

export async function listWhatsAppMappings() {
  const response = await apiFetchResponse("/admin/whatsapp-mappings");
  return decodeJsonResponse<WhatsAppUserMappingListResponse>(response, whatsAppUserMappingListResponseSchema, 200);
}

export async function updateWhatsAppMapping(mappingId: string, payload: WhatsAppUserMappingUpdateRequest) {
  const response = await apiFetchResponse(`/admin/whatsapp-mappings/${encodeURIComponent(mappingId)}`, {
    method: "PATCH",
    body: serializeRequest(whatsAppUserMappingUpdateRequestSchema, payload),
  });
  return decodeJsonResponse<{ mapping: WhatsAppUserMapping }>(response, whatsAppUserMappingResponseSchema, 200);
}

export async function createWhatsAppMapping(payload: WhatsAppUserMappingCreateRequest) {
  const response = await apiFetchResponse("/admin/whatsapp-mappings", {
    method: "POST",
    body: serializeRequest(whatsAppUserMappingCreateRequestSchema, payload),
  });
  return decodeJsonResponse<{ mapping: WhatsAppUserMapping }>(response, whatsAppUserMappingResponseSchema, 200);
}

export async function updateWhatsAppMappingDeliverySettings(
  mappingId: string,
  payload: WhatsAppDeliverySettingsUpdateRequest,
) {
  const response = await apiFetchResponse(
    `/admin/whatsapp-mappings/${encodeURIComponent(mappingId)}/delivery-settings`,
    {
      method: "PATCH",
      body: serializeRequest(whatsAppDeliverySettingsUpdateRequestSchema, payload),
    },
  );
  return decodeJsonResponse<{ mapping: WhatsAppUserMapping }>(response, whatsAppUserMappingResponseSchema, 200);
}

export async function deleteWhatsAppMapping(mappingId: string): Promise<void> {
  await apiFetch<unknown>(`/admin/whatsapp-mappings/${encodeURIComponent(mappingId)}`, {
    method: "DELETE",
  });
}

export function listWhatsAppMessages(limit = 50) {
  const q = new URLSearchParams({ limit: String(limit) });
  return apiFetch<WhatsAppMessageTraceListResponse>(`/admin/whatsapp-messages?${q.toString()}`);
}

export function listWhatsAppDeliveries(payload: { limit?: number; recipient?: string | null }) {
  const q = new URLSearchParams();
  q.set("limit", String(payload.limit ?? 20));
  if (payload.recipient) {
    q.set("recipient", payload.recipient);
  }
  return apiFetch<WhatsAppDeliveryListResponse>(`/admin/whatsapp-deliveries?${q.toString()}`);
}

export async function deleteWhatsAppDelivery(deliveryId: string): Promise<void> {
  await apiFetch<unknown>(`/admin/whatsapp-deliveries/${encodeURIComponent(deliveryId)}`, {
    method: "DELETE",
  });
}

export async function deleteAllWhatsAppDeliveries(): Promise<void> {
  await apiFetch<unknown>("/admin/whatsapp-deliveries", {
    method: "DELETE",
  });
}

export function resetAndResendWhatsAppDelivery(deliveryId: string) {
  return apiFetch<WhatsAppNotificationResponse>(
    `/admin/whatsapp-deliveries/${encodeURIComponent(deliveryId)}/reset-resend`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function resetAndResendAllWhatsAppDeliveries() {
  return apiFetch<WhatsAppDeliveryListResponse>("/admin/whatsapp-deliveries/reset-resend-all", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function sendWhatsAppTestNotification(payload: {
  to: string;
  profile?: "executive" | "operations" | "analyst" | "admin";
  summary: string;
  reportId?: string | null;
  sendTodayReports?: boolean;
  podcastUrl?: string | null;
  dashboardUrl?: string | null;
  charts?: string[];
}) {
  return apiFetch<WhatsAppNotificationResponse>("/notifications/whatsapp/aquaportal", {
    method: "POST",
    body: JSON.stringify({
      to: payload.to,
      profile: payload.profile ?? "admin",
      summary: payload.summary,
      reportId: payload.reportId ?? null,
      sendTodayReports: payload.sendTodayReports ?? false,
      podcastUrl: payload.podcastUrl ?? null,
      dashboardUrl: payload.dashboardUrl ?? null,
      charts: payload.charts ?? [],
      testMode: true,
    }),
  });
}

export function createUser(payload: {
  name: string;
  email: string;
  role: string;
  accessLevel: "read" | "read_write" | "admin";
}) {
  return apiFetch<{ user: unknown }>("/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateUser(
  userId: string,
  payload: { name: string; email: string; role: string; accessLevel: "read" | "read_write" | "admin" },
) {
  return apiFetch<{ user: unknown }>(`/users/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteUser(userId: string): Promise<void> {
  await apiFetch<unknown>(`/users/${encodeURIComponent(userId)}`, {
    method: "DELETE",
  });
}
